import { useState } from 'react'

import classNames from 'classnames'

import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { useScrollPosition } from '~/utils/useScrollPosition'

import type { SubNavItems } from '../../Header/Header'
import type { PageNavProps } from '../../PageNav/PageNav'
import PageNav from '../../PageNav/PageNav'

import styles from './InPageSubNav.module.css'

export interface InPageSubNavProps {
  title: string
  items: PageNavProps['items']
  position: number
  subNavItems: SubNavItems
  moduleId: string
  classes?: any
}

export default function InPageSubNav({
  title,
  items = [],
  classes,
  position,
  subNavItems,
  moduleId,
}: InPageSubNavProps) {
  const isLargeDevice = useMediaQuery(`(min-width: ${bp.bpMedium})`)
  const { mainHeaderVisible } = useInPageNavContext()

  const [isSticky, setIsSticky] = useState(false)
  const [currentSection, setCurrentSection] = useState('')

  /**
   * Set the sticky nature of the sub nav by the position
   */
  useScrollPosition(
    ({ currPos }) => {
      if (subNavItems) {
        const itemKeys = Object.keys(subNavItems)
        const index = itemKeys.indexOf(moduleId)

        const roundedCurrPosY = Math.abs(Math.round(currPos.y))
        const startPosition = Math.round(Number(position))
        const endPosition = itemKeys[index + 1]
          ? Math.round(Number(subNavItems[itemKeys[index + 1]].position))
          : Number.POSITIVE_INFINITY // Use a large number if there's no next item

        if (roundedCurrPosY >= startPosition && roundedCurrPosY < endPosition) {
          setIsSticky(true)
          return
        } else {
          setIsSticky(false)
          return
        }
      }
    },
    [subNavItems, setIsSticky, isSticky]
  )

  /**
   * Set the currently active section to assign to the lozenge
   */
  useScrollPosition(
    ({ currPos }) => {
      const roundedCurrPosY = Math.abs(Math.round(currPos.y))
      let currentSlug = ''

      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const nextItem = items[i + 1]

        if (item.position === undefined) continue

        if (
          !nextItem ||
          nextItem.position === undefined ||
          roundedCurrPosY < nextItem.position
        ) {
          if (roundedCurrPosY >= item.position) {
            currentSlug = item.slug
            break
          }
        }
      }

      setCurrentSection(currentSlug)
    },
    [items]
  )

  if (isLargeDevice || items.length === 0) return null

  return (
    <div
      className={classNames(
        styles.base,
        styles.isSticky,
        classes,
        mainHeaderVisible && styles.isMainHeaderVisible
      )}
    >
      <PageNav
        title={title}
        items={items}
        isStacked={false}
        variant="internal"
        activeSection={currentSection}
      />
    </div>
  )
}
